import React from 'react';
import { NEW_END_SENTINEL } from '../../utils/accuracyGate';

export interface DecisionPathFields {
  isDecision: boolean;
  yes: string;
  no: string;
  yesLabel: string;
  noLabel: string;
}

interface StepOption {
  id: string;
  name: string;
}

interface DecisionPathEditorProps {
  fields: DecisionPathFields;
  steps: StepOption[];
  selfId?: string;
  suggestedYesId?: string;
  disabled?: boolean;
  onChange: (fields: DecisionPathFields) => void;
}

/**
 * Mark-as-decision + YES/NO pickers. Labels default empty — do not prefill
 * invented routing text. "new End" is a picker option, never a silent insert.
 */
const DecisionPathEditor: React.FC<DecisionPathEditorProps> = ({
  fields,
  steps,
  selfId,
  suggestedYesId,
  disabled,
  onChange,
}) => {
  const options = steps.filter((s) => s.id && s.id !== selfId);

  const set = (patch: Partial<DecisionPathFields>) => onChange({ ...fields, ...patch });

  const toggle = (checked: boolean) => {
    if (!checked) {
      set({ isDecision: false, yes: '', no: '', yesLabel: '', noLabel: '' });
      return;
    }
    set({
      isDecision: true,
      yes: fields.yes || suggestedYesId || '',
      no: fields.no || '',
      yesLabel: fields.yesLabel || '',
      noLabel: fields.noLabel || '',
    });
  };

  return (
    <div className="space-y-2 pt-1">
      <label className="flex items-center gap-2 text-xs font-medium text-gray-800">
        <input
          type="checkbox"
          checked={fields.isDecision}
          disabled={disabled}
          onChange={(e) => toggle(e.target.checked)}
          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        Mark as decision (gateway)
      </label>
      {fields.isDecision && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-1">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">YES →</label>
            <select
              value={fields.yes}
              disabled={disabled}
              onChange={(e) => set({ yes: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select step…</option>
              {options.map((s) => (
                <option key={`yes-${s.id}`} value={s.id}>{s.name}</option>
              ))}
              <option value={NEW_END_SENTINEL}>new End</option>
            </select>
            <input
              type="text"
              value={fields.yesLabel}
              disabled={disabled}
              onChange={(e) => set({ yesLabel: e.target.value })}
              className="mt-1 w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="YES label (optional — leave empty)"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">NO →</label>
            <select
              value={fields.no}
              disabled={disabled}
              onChange={(e) => set({ no: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">Select step…</option>
              {options.map((s) => (
                <option key={`no-${s.id}`} value={s.id}>{s.name}</option>
              ))}
              <option value={NEW_END_SENTINEL}>new End</option>
            </select>
            <input
              type="text"
              value={fields.noLabel}
              disabled={disabled}
              onChange={(e) => set({ noLabel: e.target.value })}
              className="mt-1 w-full px-3 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="NO label (optional — leave empty)"
            />
          </div>
          <p className="sm:col-span-2 text-[11px] text-gray-500">
            Do not invent a branch to “pass.” Empty NO is valid when the source has no diamonds. Picking “new End” creates a dedicated End on save.
          </p>
        </div>
      )}
    </div>
  );
};

export default DecisionPathEditor;
export type { DecisionPathEditorProps, StepOption };
